import {
    useListWorkflowExecutions,
    type PlainWorkflow,
    type WorkflowExecutionsFilter,
} from "@/queries/automation-queries";
import { useMemo, useState, type FC } from "react";
import { Link } from "@/components/flexkit/Link";
import { WorkflowExecutionsCard } from "@/components/automations/AutomationExecutionCard";
import { workflowExecutionsUrl, getWorkflowTriggerDescription, isWorkflowDeleting } from "@/utils/automations";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { Button } from "@/components/flexkit/Button";
import { Command, CommandEmpty, CommandInput, CommandList } from "@/components/flexkit/Command";
import { Command as CommandPrimitive } from "cmdk";
import { standaloneInputClassName } from "@/components/podkit/forms/input-classes";
import { cn } from "@/components/podkit/lib/cn";
import {
    WorkflowExecutionStatusFilter,
    WorkflowExecutionStatusFilterValue,
} from "@/components/automations/AutomationExecutionStatusFilter";
import { WorkflowExecutionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { OrganizationMember } from "gitpod-next-api/gitpod/v1/organization_pb";
import { formatError } from "@/utils/errors";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { IconArrowDown } from "@/assets/icons/geist/IconArrowDown";
import { RunAutomationButton } from "@/routes/automations/automation-details-page/RunAutomationButton";

/**
 * Maps the UI status filter to API filter parameters
 */
function statusFilterToApiFilter(
    statusFilter: WorkflowExecutionStatusFilterValue,
): WorkflowExecutionsFilter | undefined {
    switch (statusFilter) {
        case WorkflowExecutionStatusFilterValue.Running:
            return {
                statusPhases: [WorkflowExecutionPhase.RUNNING],
            };
        case WorkflowExecutionStatusFilterValue.Completed:
            return {
                statusPhases: [WorkflowExecutionPhase.COMPLETED],
                hasFailedActions: false,
            };
        case WorkflowExecutionStatusFilterValue.Failed:
            return {
                statusPhases: [WorkflowExecutionPhase.COMPLETED],
                hasFailedActions: true,
            };
        case WorkflowExecutionStatusFilterValue.All:
        default:
            return undefined;
    }
}

type WorkflowExecutionsListProps = {
    members?: PlainMessage<OrganizationMember>[];
    hasManualTrigger: boolean;
    workflow?: PlainWorkflow;
};

export const WorkflowExecutionsList: FC<WorkflowExecutionsListProps> = ({ workflow, members, hasManualTrigger }) => {
    const [searchValue, setSearchValue] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<WorkflowExecutionStatusFilterValue>(
        WorkflowExecutionStatusFilterValue.All,
    );

    const apiFilter = useMemo(() => statusFilterToApiFilter(statusFilter), [statusFilter]);

    const {
        data: executions,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isPending,
    } = useListWorkflowExecutions({
        enabled: !!workflow?.id,
        workflowId: workflow?.id || "",
        filter: apiFilter,
    });

    const isDeleting = isWorkflowDeleting(workflow);

    // Client-side search filtering only (status filtering is done server-side)
    const filteredExecutions = useMemo(() => {
        if (!searchValue) {
            return executions;
        }
        return executions.filter((execution) => execution.id.toLowerCase().includes(searchValue.toLowerCase()));
    }, [executions, searchValue]);

    if (error) {
        return (
            <EmptyStateCard
                title="Failed to load executions"
                description={formatError(error)}
                data-testid="workflow-executions-error-state"
            >
                {refetch && (
                    <Button
                        variant="secondary"
                        LeadingIcon={IconRefresh}
                        onClick={() => refetch()}
                        data-tracking-id="retry-load-executions"
                    >
                        Retry
                    </Button>
                )}
            </EmptyStateCard>
        );
    }

    const isFiltered = statusFilter !== WorkflowExecutionStatusFilterValue.All;

    if (executions.length === 0 && !isFiltered) {
        const triggerDescription = getWorkflowTriggerDescription(workflow);
        const description = hasManualTrigger
            ? "Run this automation to create a new execution. It will run all defined steps across your configured contexts and report progress as it completes."
            : `${triggerDescription} It will run all defined steps across your configured contexts and report progress as it completes.`;

        return (
            <EmptyStateCard
                title="No executions yet"
                description={description}
                data-testid="workflow-executions-empty-state"
            >
                {hasManualTrigger && workflow && (
                    <RunAutomationButton
                        workflow={workflow}
                        disabled={isDeleting}
                        data-tracking-id="run-workflow-empty-state"
                    >
                        Run Automation
                    </RunAutomationButton>
                )}
            </EmptyStateCard>
        );
    }

    return (
        <div>
            <Command
                className="flex-col gap-3 overflow-visible border-none bg-transparent shadow-none"
                data-testid="workflow-executions-list"
            >
                <div className="flex items-center justify-between gap-3">
                    <CommandInput
                        placeholder="Search executions"
                        value={searchValue}
                        onValueChange={setSearchValue}
                        containerClassName={cn(
                            standaloneInputClassName,
                            "max-w-none *:p-0",
                            "read-only:bg-inherit read-only:cursor-auto read-only:text-inherit",
                        )}
                    />
                    <WorkflowExecutionStatusFilter value={statusFilter} onChange={setStatusFilter} />
                </div>
                {!isPending && (
                    <CommandList defaultChecked={false} className="px-1 py-1 *:flex *:flex-col *:gap-2">
                        <CommandEmpty className="w-full text-center text-base text-content-muted">
                            No executions found
                            {searchValue && (
                                <>
                                    {" "}
                                    matching <span className="font-medium text-content-strong">{searchValue}</span>
                                </>
                            )}
                        </CommandEmpty>
                        {filteredExecutions.map((execution) => (
                            <CommandPrimitive.Item key={execution.id} value={execution.id} className="group" asChild>
                                <Link to={workflowExecutionsUrl(execution)} className="flex-1">
                                    <WorkflowExecutionsCard execution={execution} members={members} />
                                </Link>
                            </CommandPrimitive.Item>
                        ))}
                    </CommandList>
                )}
            </Command>
            {hasNextPage && (
                <div className="flex w-full justify-center pt-3">
                    <Button
                        variant="ghost"
                        onClick={() => fetchNextPage?.()}
                        disabled={isFetchingNextPage}
                        data-tracking-id="load-more-workflow-executions"
                    >
                        <IconArrowDown size="lg" />{" "}
                        {isFetchingNextPage ? <span>Loading...</span> : <span>Load more</span>}
                    </Button>
                </div>
            )}
        </div>
    );
};
